<template>
  <Teleport to="body">
    <transition name="app-dialog-fade">
      <div
        v-if="dialog.current"
        class="app-dialog-backdrop"
        @click.self="cancel"
        @keydown.esc="cancel"
      >
        <div
          class="app-dialog"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="dialog.current.title ? 'app-dialog-title' : undefined"
          aria-describedby="app-dialog-message"
        >
          <h2 v-if="dialog.current.title" id="app-dialog-title" class="app-dialog-title font-cinzel">
            {{ dialog.current.title }}
          </h2>
          <p id="app-dialog-message" class="app-dialog-message">{{ dialog.current.message }}</p>

          <input
            v-if="dialog.current.kind === 'prompt'"
            ref="inputEl"
            v-model="dialog.current.value"
            class="form-control app-dialog-input"
            :placeholder="dialog.current.placeholder"
            @keydown.enter.prevent="accept"
          />

          <div class="app-dialog-actions">
            <button type="button" class="btn btn-outline-secondary" @click="cancel">
              {{ dialog.current.cancelText }}
            </button>
            <button
              ref="confirmEl"
              type="button"
              class="btn"
              :class="dialog.current.danger ? 'btn-danger' : 'btn-dark'"
              @click="accept"
            >
              {{ dialog.current.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { nextTick, ref, watch } from 'vue'
import { useDialogStore } from '../stores/dialogStore'

const dialog = useDialogStore()
const inputEl = ref(null)
const confirmEl = ref(null)
let lastFocus = null

function accept() {
  const current = dialog.current
  if (!current) return
  dialog.close(current.kind === 'prompt' ? current.value : true)
}

function cancel() {
  const current = dialog.current
  if (!current) return
  dialog.close(current.kind === 'prompt' ? null : false)
}

function onKey(event) {
  if (event.key === 'Escape') cancel()
}

watch(() => dialog.current, async (value) => {
  if (value) {
    lastFocus = document.activeElement
    window.addEventListener('keydown', onKey)
    await nextTick()
    ;(inputEl.value || confirmEl.value)?.focus()
  } else {
    window.removeEventListener('keydown', onKey)
    lastFocus?.focus?.()
  }
})
</script>

<style scoped>
.app-dialog-backdrop {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  background: rgba(0, 0, 0, 0.55);
}
.app-dialog {
  width: 100%;
  max-width: 440px;
  padding: 1.5rem;
  border-radius: 1rem;
  border: 1px solid var(--be-border);
  background-color: var(--be-surface);
  color: var(--be-text);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.35);
}
.app-dialog-title {
  font-size: 1.15rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}
.app-dialog-message {
  margin-bottom: 1.25rem;
  white-space: pre-line;
  color: var(--be-text);
}
.app-dialog-input {
  margin-bottom: 1.25rem;
  font-size: 16px; /* evita zoom automático no iPhone */
}
.app-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
.app-dialog-actions .btn {
  min-height: 44px;
  min-width: 96px;
}
/* No celular os botões ocupam a largura toda, fáceis de tocar */
@media (max-width: 575.98px) {
  .app-dialog-backdrop { align-items: flex-end; }
  .app-dialog-actions { flex-direction: column-reverse; }
  .app-dialog-actions .btn { width: 100%; }
}
.app-dialog-fade-enter-active,
.app-dialog-fade-leave-active { transition: opacity 0.15s ease; }
.app-dialog-fade-enter-from,
.app-dialog-fade-leave-to { opacity: 0; }
@media (prefers-reduced-motion: reduce) {
  .app-dialog-fade-enter-active,
  .app-dialog-fade-leave-active { transition: none; }
}
</style>
